import { useRef } from 'react';
import { Navigate, Outlet, useSearchParams } from 'react-router-dom';
import PageLoader from '../pages/page-loader';
import { useAuthStore } from '../stores/auth.store';
import { signedInTarget } from '../utils/safe-redirect';

/**
 * Wraps /login and /register: a visitor who is ALREADY signed in when they
 * open one is sent on — to a safe `?redirect=` when the link carried one, to
 * SIGNED_IN_HOME (/app) otherwise.
 *
 * Decided here for that visitor, not only in the pages: this wrapper swaps
 * the page for <Navigate> before a page-level effect could run, which once
 * lost the one-tap RSVP's group page and its "You're marked …" notice
 * (phone pass P3-1, shots 16/17). ProtectedRoute builds the redirect.
 *
 * A session that arrives WHILE the page is open (the page's own submit, an
 * OAuth return) is the page's to finish: the store sets the session before
 * register()/login() returns, and the page still has post-auth steps to run
 * (the verification-email advice, the welcome) before it follows the same
 * `signedInTarget` (fix round items 4 / 9). Swapping it out here mid-submit
 * ran those steps from a page that no longer existed, after the landing.
 */
export default function PublicRoute() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  const [searchParams] = useSearchParams();
  // Signed in at the first settled render? Fixed for the life of this mount
  // (login ↔ register share it).
  const signedInOnEntry = useRef<boolean | null>(null);

  if (isLoading) {
    return <PageLoader />;
  }
  if (signedInOnEntry.current === null) signedInOnEntry.current = isAuthenticated;

  if (isAuthenticated && signedInOnEntry.current) {
    return <Navigate to={signedInTarget(searchParams.get('redirect'))} replace />;
  }
  return <Outlet />;
}
